import { Button, Checkbox, InputNumber, Table } from "antd";
import { observer } from "mobx-react-lite";
import React from "react";
import {
  computeInfraRowCost,
  computeInfraTotal,
} from "../calculations";
import type { FestivalCalcStore } from "../store";
import { AutocompleteInput } from "./shared/AutocompleteInput";
import { SectionWrapper } from "./shared/SectionWrapper";
import { TagPriceEditor } from "./shared/TagPriceEditor";
import { UserBadge } from "./shared/UserBadge";

interface Props {
  store: FestivalCalcStore;
}

export const InfraSection = observer(({ store }: Props) => {
  const { infraItems, tags, activeTagId } = store;

  const allTypes = infraItems.map((i) => i.itemType).filter(Boolean);
  const allNames = infraItems.map((i) => i.name).filter(Boolean);
  const total = computeInfraTotal(infraItems, activeTagId, tags);

  const columns = [
    {
      title: "Aktiv",
      dataIndex: "active",
      width: 60,
      render: (_: any, item: (typeof infraItems)[0]) => (
        <Checkbox
          checked={item.active}
          onChange={(e) =>
            item.objectId && store.updateInfraItem(item.objectId, { active: e.target.checked })
          }
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "name",
      render: (_: any, item: (typeof infraItems)[0]) => (
        <AutocompleteInput
          value={item.name}
          onChange={(v) => item.objectId && store.updateInfraItem(item.objectId, { name: v })}
          suggestions={allNames}
          placeholder="Item"
        />
      ),
    },
    {
      title: "Typ",
      dataIndex: "itemType",
      render: (_: any, item: (typeof infraItems)[0]) => (
        <AutocompleteInput
          value={item.itemType}
          onChange={(v) => item.objectId && store.updateInfraItem(item.objectId, { itemType: v })}
          suggestions={allTypes}
          placeholder="Typ"
        />
      ),
    },
    {
      title: "Preis",
      dataIndex: "tagPrices",
      render: (_: any, item: (typeof infraItems)[0]) => {
        const { resolved } = computeInfraRowCost(item, activeTagId, tags);
        return (
          <TagPriceEditor
            tagPrices={item.tagPrices}
            allTags={tags}
            priceField="amount"
            resolved={resolved}
            onChange={(updated) =>
              item.objectId && store.updateInfraItem(item.objectId, { tagPrices: updated as any })
            }
          />
        );
      },
    },
    {
      title: "Ist-Kosten",
      key: "actualCost",
      width: 160,
      render: (_: any, item: (typeof infraItems)[0]) => {
        const { cost } = computeInfraRowCost(item, activeTagId, tags);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Checkbox
              checked={item.isActual}
              onChange={(e) => {
                const patch: Partial<(typeof infraItems)[0]> = { isActual: e.target.checked };
                if (e.target.checked && item.actualCost === null) patch.actualCost = cost;
                item.objectId && store.updateInfraItem(item.objectId, patch);
              }}
            />
            {item.isActual && (
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={item.actualCost ?? 0}
                onChange={(v) =>
                  item.objectId && store.updateInfraItem(item.objectId, { actualCost: v ?? 0 })
                }
                style={{ width: 95 }}
                addonAfter="€"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "createdBy",
      width: 40,
      render: (_: any, item: (typeof infraItems)[0]) => <UserBadge user={item.createdBy} />,
    },
    {
      title: "",
      key: "delete",
      width: 40,
      render: (_: any, item: (typeof infraItems)[0]) => (
        <Button
          type="text"
          danger
          size="small"
          onClick={() => item.objectId && store.deleteInfraItem(item.objectId)}
        >
          ✖
        </Button>
      ),
    },
  ];

  return (
    <SectionWrapper title="Infrastruktur" total={total} onAdd={() => store.addInfraItem()}>
      <Table
        dataSource={infraItems}
        columns={columns}
        rowKey={(r) => r.objectId ?? Math.random().toString()}
        pagination={false}
        size="small"
        rowClassName={(item) => (item.active ? "" : "ant-table-row-disabled")}
        style={{ opacity: 1 }}
      />
    </SectionWrapper>
  );
});
